import { NavLink } from "react-router-dom";
import { cx } from "../../lib/utils";
import { useTheme } from "../../lib/use-theme";
import { Icon } from "../ui/icon";

interface RailLink {
  to: string;
  label: string;
  icon: string;
}

// Conversation detail (/c/:id) is deliberately not a rail link — it's reached
// from Inbox/Leads, not navigated to directly. Matches the reference
// mockups' three-item nav exactly.
const LINKS: RailLink[] = [
  { to: "/", label: "Inbox", icon: "inbox" },
  { to: "/all", label: "Leads", icon: "group" },
  { to: "/campaigns", label: "Campaigns", icon: "send" },
];

const NAV_BASE =
  "flex items-center gap-spacing-md px-spacing-md py-spacing-xs rounded-xl font-label-md text-label-md transition-colors";
const NAV_ACTIVE = "bg-primary-container text-on-primary font-medium shadow-sm";
const NAV_INACTIVE = "text-on-surface-variant hover:bg-surface-container hover:text-on-surface";

interface RailProps {
  // Ignored above `md` — the rail is always visible there, so open/onClose
  // only matter for the off-canvas mobile drawer.
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onCollapsedChange: () => void;
}

export function Rail({ open, onClose, collapsed, onCollapsedChange }: RailProps) {
  const [theme, toggleTheme] = useTheme();

  return (
    <>
      {/* Backdrop: mobile-only, only rendered while the drawer is open, tap
          to dismiss. Absent above `md` since the rail is never a drawer there. */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={cx(
          "fixed left-0 top-0 h-screen w-64 bg-surface-container-lowest border-r border-outline-variant/40 z-50 flex flex-col justify-between select-none",
          collapsed ? "md:w-16" : "md:w-64",
          "transition-[transform,width] duration-200 ease-out md:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
      <div className="flex flex-col">
        <div className={cx("h-14 px-spacing-base flex items-center border-b border-outline-variant/30", collapsed ? "justify-center" : "justify-between")}>
          <div className="flex items-center gap-spacing-sm overflow-hidden">
            <div className="w-7 h-7 rounded-xl bg-primary flex items-center justify-center text-on-primary font-headline-sm text-headline-sm">
              V
            </div>
            <div className={cx("flex items-baseline gap-spacing-2xs", collapsed && "hidden")}>
              <span className="font-headline-md text-headline-sm text-on-surface tracking-tight">Vantage</span>
              <span className="px-1.5 py-0.5 rounded-lg bg-surface-container font-label-sm text-label-sm text-on-surface-variant">
                v2.4
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onCollapsedChange}
            className="hidden md:flex w-7 h-7 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container hover:text-on-surface"
            title={collapsed ? "Expand navigation" : "Collapse navigation"}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          >
            <Icon name={collapsed ? "chevron_right" : "chevron_left"} className="text-[18px]" />
          </button>
        </div>
        <div className="p-spacing-sm">
          <nav className="flex flex-col gap-spacing-3xs" aria-label="Primary">
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={onClose}
                className={({ isActive }) => cx(NAV_BASE, collapsed && "justify-center px-0", isActive ? NAV_ACTIVE : NAV_INACTIVE)}
              >
                <Icon name={link.icon} className="text-[18px]" />
                <span className={cx(collapsed && "hidden")}>{link.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      <div className="p-spacing-sm border-t border-outline-variant/30 flex flex-col gap-spacing-3xs">
        {/* Persists (localStorage) and toggles the `.dark` class the reference
            mockups themselves never styled beyond this control — no dark M3
            palette was supplied alongside them, so `dark:` variants are a
            follow-up once one exists. The toggle is real, not decorative. */}
        <button
          type="button"
          onClick={toggleTheme}
          className={cx("w-full flex items-center justify-between px-spacing-md py-spacing-xs rounded-xl text-on-surface-variant hover:bg-surface-container hover:text-on-surface font-label-md text-label-md transition-colors", collapsed && "justify-center px-0")}
        >
          <span className="flex items-center gap-spacing-md">
            <Icon name={theme === "light" ? "light_mode" : "dark_mode"} className="text-[18px]" />
            <span className={cx(collapsed && "hidden")}>Theme</span>
          </span>
          <span className={cx("font-label-sm text-label-sm text-on-surface-variant capitalize", collapsed && "hidden")}>{theme}</span>
        </button>
        {/* A plain form POST, not a router Link or GET anchor: server.ts's
            /logout route only accepts POST (clearing the session cookie is
            not a safe GET action), and it's server.ts — not App.tsx — that
            owns this, so this deliberately leaves the SPA rather than trying
            to client-route somewhere that doesn't exist. A GET to /login
            here would NOT log anyone out: CONSOLE_AUTH_MODE=workos redirects
            /login straight to AuthKit regardless of session state, which
            silently completes against the browser's still-live Google
            session and logs the same user back in instead of signing out. */}
        <form action="/logout" method="post">
          <button
            type="submit"
            className={cx("w-full flex items-center gap-spacing-md px-spacing-md py-spacing-xs rounded-xl text-on-surface-variant hover:bg-surface-container hover:text-on-surface font-label-md text-label-md transition-colors", collapsed && "justify-center px-0")}
          >
            <Icon name="logout" className="text-[18px]" />
            <span className={cx(collapsed && "hidden")}>Log out</span>
          </button>
        </form>
      </div>
      </aside>
    </>
  );
}
